import { supabase } from './supabaseClient';

const BUCKET = 'project-files';

// Ліміти для email-юзерів
export const EMAIL_USER_LIMITS = {
  estimateMaxFiles: 10,
  estimateMaxFileSize: 2 * 1024 * 1024, // 2 MB
  projectMaxFileSize: 10 * 1024 * 1024,  // 10 MB
};

/**
 * Завантажити файл в Supabase Storage
 * @param {File} file - файл
 * @param {string} userId - ID юзера
 * @param {string} folder - підпапка (наприклад projectId/estimateId)
 * @returns {{ path, url, name, size, type }}
 */
export const uploadFileToSupabase = async (file, userId, folder = '') => {
  // Sanitize: тільки латиниця, цифри, тире, підкреслення, крапка
  const safeName = file.name
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_/, '');
  // Flat folder — замінюємо / на _
  const safeFolder = folder.replace(/\//g, '_');
  const filePath = `${userId}/${safeFolder ? safeFolder + '_' : ''}${Date.now()}_${safeName}`;
  
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false
    });

  if (error) throw error;

  const { data: urlData } = supabase.storage
    .from(BUCKET)
    .getPublicUrl(filePath);

  // Для приватного bucket використовуємо signed URL
  const { data: signedData } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(filePath, 60 * 60 * 24 * 365); // 1 рік

  return {
    path: filePath,
    url: signedData?.signedUrl || urlData?.publicUrl || '',
    name: file.name,
    size: file.size,
    type: file.type,
    storage: 'supabase'
  };
};

/**
 * Видалити файл з Supabase Storage
 * @param {string} filePath - шлях файлу
 */
export const deleteFileFromSupabase = async (filePath) => {
  const { error } = await supabase.storage
    .from(BUCKET)
    .remove([filePath]);
  
  if (error) throw error;
};

/**
 * Отримати signed URL для файлу
 * @param {string} filePath - шлях файлу
 * @returns {string} URL
 */
export const getSupabaseFileUrl = async (filePath) => {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(filePath, 60 * 60); // 1 година

  if (error) throw error;
  return data.signedUrl;
};

/**
 * Перевірити ліміт файлів для estimate
 * @param {number} currentCount - поточна кількість файлів
 * @param {File} file - файл для завантаження
 * @returns {{ ok: boolean, error?: string }}
 */
export const checkEstimateFileLimit = (currentCount, file) => {
  if (currentCount >= EMAIL_USER_LIMITS.estimateMaxFiles) {
    return { ok: false, error: `Максимум ${EMAIL_USER_LIMITS.estimateMaxFiles} файлів на звіт` };
  }
  if (file.size > EMAIL_USER_LIMITS.estimateMaxFileSize) {
    const maxMB = EMAIL_USER_LIMITS.estimateMaxFileSize / (1024 * 1024);
    return { ok: false, error: `Максимальний розмір файлу ${maxMB} МБ` };
  }
  return { ok: true };
};

/**
 * Перевірити ліміт файлу для project
 * @param {File} file - файл для завантаження
 * @returns {{ ok: boolean, error?: string }}
 */
export const checkProjectFileLimit = (file) => {
  if (file.size > EMAIL_USER_LIMITS.projectMaxFileSize) {
    const maxMB = EMAIL_USER_LIMITS.projectMaxFileSize / (1024 * 1024);
    return { ok: false, error: `Максимальний розмір файлу ${maxMB} МБ` };
  }
  return { ok: true };
};
